import {Breakpoints} from '@angular/cdk/layout';
import {ChangeDetectionStrategy, Component, Inject} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';

import {ellipsisString} from '@atmi/core/utils';

import {firstValueFrom, from} from 'rxjs';

import {RoleService} from '@sim/role/services/role.service';
import {
  basicDeleteTemplateOptions,
  basicEditTemplateOptions,
  TableContextMenu,
  TableField,
  TableFilterType,
  TableSortType,
  UiTableComponent
} from '@ui/table/table';
import {BaseTableFilteredComponent} from '@ui/ui.module';

import {basePath, filePath, SwalService} from '@atmi/core';
// TODO: Try to avoid this kind of bind, this make module not really extendible (maybe move this dialog in media module)

import {MediaFiltersComponent} from '@sim/media/components/media-filters/media-filters.component';
import {Media, MediaFilter, MediaFilterMeta} from '@sim/media/models/media';
import {MediaService} from '@sim/media/services/media.service';
import {extractMediaTypeFromPath, getErroredIdsMap, IObjectTypeToUriPath, MediaDeleteErrorInfo, DELETE_CONSTRAINT_MARKER} from '@sim/media/utils';
import {UIMediaService} from '../../services/ui-media.service';
import {first} from "rxjs/operators";
import {DOCUMENT, LocationStrategy} from "@angular/common";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-media',
  styleUrls: ['./media.component.scss'],
  templateUrl: './media.component.html',
})
export class MediaComponent extends BaseTableFilteredComponent<Media, MediaFilter> implements UiTableComponent<Media> {

  readonly MobileFiltersComponent = MediaFiltersComponent;

  fields: TableField<Media>[] = [
    {
      header: '',
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'checkbox',
      selectable: true,
    },
    {
      context: (x) => `${this.fileUrl}${x.previewUrl}`,
      header: this.translate.instant('Antemprima'),
      key: 'previewUrl',
      template: 'img',
    },
    {
      context: (x) => ellipsisString(x.name || '', 20),
      header: this.translate.instant('Nome'),
      key: 'name',
    },
    {
      context: (x) => x.type || extractMediaTypeFromPath(x.path),
      header: this.translate.instant('Tipo'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'type',
    },
    {
      context: (x) => x.tags ? x.tags.slice(0, 5).join(', ') : '',
      header: this.translate.instant('Tags'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'tags',
    },
    {
      header: this.translate.instant('Note'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'note',
    },
    {
      header: this.translate.instant('Data caricamento'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'createdAt',
      sortable: true,
      template: 'date-time',
    },
    {
      align: 'right',
      header: '',
      key: 'context',
      showBreakpoints: [Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium],
      template: 'context',
    },
  ];

  readonly pager$ = ((page?: number, limit?: number, sort?: TableSortType, filter?: TableFilterType) =>
      this.mediaService.index({sort, page, limit, filter})
  );

  readonly mediaContextMenus: Array<TableContextMenu<Media>> = [
    {
      action: (media) => this.uiMediaService.dispatchViewMediaAction(media),
      label: this.translate.instant('Visualizza'),
    },
    {
      action: (media) => this.uiMediaService.dispatchDownloadMediaAction(media),
      label: this.translate.instant('Scarica'),
    }
  ];

  baseHref:string='/';

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly locationStrategy: LocationStrategy,
    @Inject(filePath) private readonly fileUrl: string,
    private readonly mediaService: MediaService,
    private readonly roleService: RoleService,
    private readonly uiMediaService: UIMediaService,
    protected readonly route: ActivatedRoute,
    protected readonly router: Router,
    protected readonly swalService: SwalService,
    protected readonly translate: TranslateService,
  ) {
    super(translate, router, route, MediaFilterMeta, swalService, 'manager');

    this.roleService.role$.pipe(first(role => !!role)).subscribe(() => {
      const hasUpdate = this.roleService.hasCapability('File', ['update']);
      const hasDelete = this.roleService.hasCapability('File', ['delete']);

      if (hasUpdate && !this.fields.some(f => f.key === 'edit')) {
        this.mediaContextMenus.push({
          action: (item) => this.router.navigate(['manager', item.id], { relativeTo: this.route }),
          label: this.translate.instant('Modifica'),
        });
        this.fields = [...this.fields, basicEditTemplateOptions];
      }

      if (hasDelete && !this.fields.some(f => f.key === 'delete')) {
        this.mediaContextMenus.push({
          action: (item) => item.id ? this.deleteHandler(item.id) : () => undefined,
          label: this.translate.instant('Elimina'),
        });
        this.fields = [...this.fields, basicDeleteTemplateOptions];
      }
      
      // We must re-assign fields to trigger change detection or subjects if bound to them, 
      // but modifying the array works as long as the ui-table handles mutations, 
      // or we can force an update by emitting a new value if there was a Subject.
      // But since `fields` is bound directly, updating its reference is safest.
    });

    this.baseHref = this.locationStrategy.getBaseHref();
  }


  deleteConcreteHandler(id: string) {
    return this.mediaService.remove(id);
  }

  private extractDeleteErrorDescription(err: any): string {
    return (
      err?.error?.error?.description ||
      err?.error?.message ||
      err?.message ||
      ''
    );
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  async deleteSelectedHandler() {
    const selectedIds = this.selectedItems
      .map((m) => m.id)
      .filter((id): id is string => !!id);
    const erroredIdsInfo: MediaDeleteErrorInfo[] = [];
    const genericDeleteErrors: string[] = [];
    const deleteSucceededItems: Media[] = [];
    let idx = 0;
    for (let selectedId of selectedIds) {
      try {
        await firstValueFrom(this.mediaService.remove(selectedId));
        deleteSucceededItems.push(this.selectedItems[idx]);
      } catch (err: any) {
        const errorDescription = this.extractDeleteErrorDescription(err);
        if (errorDescription.includes(DELETE_CONSTRAINT_MARKER)) {
          try {
            const parsed = getErroredIdsMap(errorDescription);
            if (Object.keys(parsed).length > 0) {
              erroredIdsInfo.push(parsed);
            } else {
              genericDeleteErrors.push(errorDescription);
            }
          } catch {
            genericDeleteErrors.push(errorDescription);
          }
        } else {
          genericDeleteErrors.push(errorDescription || 'Operazione non riuscita');
        }
      }
      idx++;
    }

    let swalTitleMessage = 'Rimozione avvenuta con successo';
    const successText = deleteSucceededItems.length ? `Media rimossi: ${deleteSucceededItems.map(item => item.name).join(', ')}` : '';
    const failedCount = erroredIdsInfo.length + genericDeleteErrors.length;

    if (failedCount === 0) {
      this.swalService.showSuccess({title: swalTitleMessage, text: successText})
    } else if (failedCount < selectedIds.length) {
      swalTitleMessage = 'Rimozione parziale';
    } else if (failedCount === selectedIds.length) {
      swalTitleMessage = 'Rimozione non riuscita';
    }


    if (failedCount !== 0) {
      const mergedErrors = erroredIdsInfo.reduce((acc: MediaDeleteErrorInfo, info: MediaDeleteErrorInfo) => {
        Object.entries(info).forEach(([objType, objList]) => {
          acc[objType] = (acc[objType] || []).concat(objList);
        });
        return acc;
      }, {} as MediaDeleteErrorInfo);

      const linksHtml = Object.keys(mergedErrors).length
        ? `<h3>Media non cancellati perche legati a oggetti</h3>${[mergedErrors].map(errInfo => {
          return Object.entries(errInfo).map(([objType, objList]) => {
            return `<h4>${objType}</h4><ul>${objList.map(obj =>
              `<li><a target="_blank" href="${this.document.location.origin}${this.baseHref}${IObjectTypeToUriPath[objType]}/${obj.id}">${obj.code}</a></li>`).join('')}</ul>`
          }).join('')
        }).join('')}`
        : '';

      const genericErrorsHtml = genericDeleteErrors.length
        ? `<h3>Dettagli errore</h3><ul>${genericDeleteErrors
            .map((message) => `<li>${this.escapeHtml(message)}</li>`)
            .join('')}</ul>`
        : '';

      const html = `<div class="errors-links">${linksHtml}${genericErrorsHtml}</div>`;

      this.swalService.showError({title: swalTitleMessage, text: successText, html})
    }

    this.reload$.next(true);
    this.selectedItems.length = 0;
  }

  deleteSelectedConcreteHandler(ids: string[]) {
    const deleteApis = ids.map((id) => firstValueFrom(this.mediaService.remove(id)));
    return from(Promise.all(deleteApis));
  }
}
